import { t } from '@moovly/i18n';
import { endOfDay, startOfDay, subYears } from 'date-fns';
import * as React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RequestStatus } from '../../../config/store';
import { RootState } from '../../../store/rootStore';
import { BarChartSet } from '../../charts/components/BarChart';
import { graphColors as getGraphColors } from '../../charts';
import { getGroupStatistics, IMetricData } from '../store/statistics.store';
import useLicenses from '../../subscriptions/hooks/useLicenses';
import { compareDatesAsc, formatDate } from '../../../utils/date';

const dataToChartData = (data: IMetricData[]) =>
  [...data]
    .sort((a, b) => compareDatesAsc(a.start, b.start))
    .map((d: any) => ({
      x: formatDate(d.start, 'MMMM yy'),
      y: d.value,
    }));

const useGroupGraphData = (months: number) => {
  const dispatch = useDispatch<any>();
  const { contracts } = useLicenses();
  const graphColors = React.useMemo(() => getGraphColors(), []);

  const subscriptionData = React.useMemo<BarChartSet[]>(() => {
    const data: BarChartSet[] = [
      {
        label: t('home.statistics.seats.assigned'),
        data: [],
        backgroundColor: graphColors[0],
      },
      {
        label: t('home.statistics.seats.available'),
        data: [],
        backgroundColor: graphColors[1],
      },
    ];

    contracts.forEach((contract) => {
      const assignedIndex = data[0].data.findIndex((d) => d.x === contract.license_type);
      if (assignedIndex >= 0) {
        data[0].data[assignedIndex].y =
          (data[0].data[assignedIndex].y as number) + contract.licenses.filter((l) => !!l.assigned_to).length;
      } else {
        data[0].data.push({
          x: contract.license_type,
          y: contract.licenses.filter((l) => !!l.assigned_to).length,
        });
      }

      const availableIndex = data[1].data.findIndex((d) => d.x === contract.license_type);
      if (availableIndex >= 1) {
        data[1].data[availableIndex].y =
          (data[1].data[availableIndex].y as number) +
          contract.licenses.filter((l) => !l.assigned_to && !l.is_expired && !!l.assignments_left).length;
      } else {
        data[1].data.push({
          x: contract.license_type,
          y: contract.licenses.filter((l) => !l.assigned_to && !l.is_expired && !!l.assignments_left).length,
        });
      }
    });

    return data;
  }, [contracts, graphColors]);

  const groupResponse = useSelector((state: RootState) => state.groupStatistics.group);
  const groupId = useSelector((state: RootState) => state.groupStatistics.activeGroupId);
  React.useEffect(() => {
    if (groupResponse.request.get.status === RequestStatus.Unsent && groupId) {
      const now = endOfDay(new Date());
      const previousYear = startOfDay(subYears(now, 1));
      dispatch(getGroupStatistics({ groupId, start: previousYear, end: now }));
    }
  }, [groupResponse, groupId, dispatch]);

  const { projectData, userData, renderData } = React.useMemo(() => {
    if (groupResponse.data) {
      return {
        projectData: [
          {
            backgroundColor: graphColors[0],
            label: t('home.statistics.projects.projects'),
            data: dataToChartData(groupResponse.data.create_project).slice(-months),
          },
        ] as BarChartSet[],
        userData: [
          {
            backgroundColor: graphColors[0],
            label: t('home.statistics.users.newUsers'),
            data: dataToChartData(groupResponse.data.create_membership).slice(-months),
          },
          {
            backgroundColor: graphColors[1],
            label: t('home.statistics.users.returningUsers'),
            data: dataToChartData(groupResponse.data.returning_membership).slice(-months),
          },
        ] as BarChartSet[],
        renderData: [
          {
            backgroundColor: graphColors[0],
            label: t('home.statistics.projects.totalRenders'),
            data: dataToChartData(groupResponse.data.create_render).slice(-months),
          },
        ] as BarChartSet[],
      };
    }
    return {};
  }, [groupResponse.data, months, graphColors]);

  return { projectData, userData, renderData, subscriptionData };
};

export default useGroupGraphData;
